import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { cn, getErrorBorderClass } from '@/lib';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export type EmailLoginFormProps = React.ComponentProps<'form'> & {
  email: string;
  password: string;
  canSubmit: boolean;
  isLoading: boolean;
  hasError?: boolean;
  errorMessage?: string | null;
  registerState?: { from?: string; email?: string; organizationName?: string };
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmitPassword: () => void;
};

export function EmailLoginForm({
  className,
  email,
  password,
  canSubmit,
  isLoading,
  hasError,
  errorMessage,
  registerState,
  onEmailChange,
  onPasswordChange,
  onSubmitPassword,
  ...props
}: EmailLoginFormProps) {
  const [showPassword, setShowPassword] = React.useState(false);

  return (
    <form
      className={cn('flex flex-col gap-6', className)}
      onSubmit={(e) => (e.preventDefault(), onSubmitPassword())}
      {...props}
    >
      {errorMessage && (
        <div className="bg-destructive/15 text-destructive rounded-md p-3 text-sm font-medium">{errorMessage}</div>
      )}
      <div className="grid gap-6">
        <div className="grid gap-3">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="name@example.com"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            autoComplete="email"
            className={getErrorBorderClass(hasError)}
            autoFocus
            required
          />
        </div>

        <div className="grid gap-3">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" state={{ email }} className="text-primary text-sm hover:underline">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              autoComplete="current-password"
              className={cn('pr-10', getErrorBorderClass(hasError))}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <Button type="submit" className="w-full" disabled={isLoading || !canSubmit}>
          {isLoading ? 'Logging in...' : 'Login'}
        </Button>

        <div className="text-center text-sm">
          <span className="text-muted-foreground">Don't have an account? </span>
          <Link to="/register" state={registerState} className="text-primary hover:underline">
            Register
          </Link>
        </div>
      </div>
    </form>
  );
}
